import { BaseClientConnector } from './BaseClientConnector.js';
import { ServerPeerConnection } from "./ServerPeerConnection.js";
import { PROTOCOLS, ProtocolArray, WEBRTC_RELIABLE_CHANNEL, ensureArrayBuffer, frameRawWithArea } from "@gotti/core";

const asyncTimeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Bounded settle after sending the deliberate-leave frame, before tearing the
 *  peer connection down — long enough for the reliable SCTP packet to leave the
 *  wire (bufferedAmount is unreliable here; see flushBeforeLeave), short enough
 *  to be imperceptible on a user-initiated quit. Only ever paid on a deliberate
 *  close()/clearGame(), never on the abnormal-drop reconnect path. */
const WEBRTC_DELIBERATE_LEAVE_SETTLE_MS = 100;

const PING_STRINGED = PROTOCOLS.PING.toString();

export type ClientConnectorWebSocketoptions = {
    wsEndpoint: string,
}

export class ClientConnectorWebRTC extends BaseClientConnector {
  private wsClient : WebSocket;
  private peerConnection : ServerPeerConnection;
  private waitPeerInterval : NodeJS.Timeout | null = null;

    public async connect(opts) {

        let protocol = 'ws:';
        if(typeof window !== "undefined" && window?.location?.protocol === 'https:') {
            protocol = 'wss:'
        }
        // console.log('url was', `${protocol}//${opts.host}:${opts.port}?privateId=${opts.privateId}&publicId=${opts.publicId}`)
        this.wsClient = new WebSocket(`${protocol}//${opts.host}:${opts.port}?privateId=${opts.privateId}&publicId=${opts.publicId}`)
      
        let resolvedPeer = false;
        let resolvedTries = 0;
        let rejected = false;
        return new Promise((resolve, reject) => {
          this.wsClient.onclose = () => {
            if(this.client.forceClosed) {
              if(this.waitPeerInterval !== null) {
                clearInterval(this.waitPeerInterval);
                this.waitPeerInterval = null;
              }
              rejected = true;
              return reject('Client force closed');
            }
            if(resolvedPeer) {
              delete this.wsClient;
            } else {
              this.waitPeerInterval = setInterval(() => {
                if(resolvedPeer) {
                  clearInterval(this.waitPeerInterval);
                  this.waitPeerInterval = null; 
                  delete this.wsClient;
                } else if(resolvedTries++ > 150) {
                  console.error('CLIENT in max resolved tries')
                  clearInterval(this.waitPeerInterval);
                  this.waitPeerInterval = null;
                  rejected = true;
                  return reject('Client couldnt connect to peer');
                }
              }, 100);
            }
          }
            this.wsClient.addEventListener('open', () => {
                this.peerConnection = new ServerPeerConnection(this.client.privateId, {}, this.wsClient);
                // Data-channel death → drive the auto-reconnect loop (mirrors the
                // websocket wiring, on the WebRTC transport's real death signal).
                // onConnectionLost is cleared FIRST by disconnect() on a
                // deliberate teardown. Scope cut: peer connections are NOT
                // auto-restored — peers re-negotiate fresh as on a first join.
                this.peerConnection.onServerDisconnected = () => this.onConnectionLost?.();
                let failTimeout: NodeJS.Timeout | null = setTimeout(() => {
                  if(!resolvedPeer && !rejected) {
                    reject('Failed to connect to peer');
                  }
                }, 7500);

                this.peerConnection.onPeerMessageHandler = (label, data) => {
                  if(resolvedPeer || failTimeout === null) {
                    return;
                  }
                  if(label !== WEBRTC_RELIABLE_CHANNEL) {
                    return;
                  }
                  const message = typeof data === 'string' ? data : data?.toString?.() ?? '';
                  if(message !== PING_STRINGED) {
                    throw new Error(`First message from server is expected to be protocol ping (${PROTOCOLS.PING})`);
                  }
                  clearTimeout(failTimeout);
                  failTimeout = null;
                  resolvedPeer = true;
                  this.peerConnection.sendReliable(message);
                  resolve(true);
                  this.peerConnection.onPeerMessageHandler = this.handlePeerMessage;
                }
            })
            let initialized = false;
            let initializing = false;
            this.wsClient.onmessage = async (event) => {
                const payload = JSON.parse(event.data.toString());
                switch(payload[0]) {
                  case PROTOCOLS.PEER_SIGNAL_REQUEST: {
                    let i = 0;
                    while(initializing && ++i < 500) {
                      await asyncTimeout(100);
                    }
                    if(initializing) {
                      throw new Error(`Still initializing`)
                    }
                    const peer = this.peerConnection;
                    // onDisconnect() may run while we await above; bail if the peer was torn down mid-message.
                    if(!peer) {
                      return;
                    }
                    if(!initialized) {
                      initializing = true;
                     // console.log('accept connection')
                      await this.peerConnection.acceptConnection();
                      initialized = true;
                      // console.log('ACCEPTED CONNECTION');
                      initializing = false;
                    }
                   // console.log('CLIENT IS HANDLING PEER SIGNAL:', payload[1]);
                    if(payload[1].sdp) {
                      // console.log('HANDLE AS SDP', payload[1].sdp)
                      peer.handleSDPSignal(payload[1].sdp);
                    } else if (payload[1].candidate) {
                      // console.log('HANDLE ICE', payload[1].candidate)
                      peer.handleIceCandidateSignal(payload[1].candidate);
                    }
                    break;
                  }
                }
            }
        })
    }
    /** Drain the peer's reliable channel before a deliberate teardown so the
     *  final CLIENT_LEAVING frame reaches the wire ahead of destroy() (which
     *  closes the SCTP transport and would otherwise drop a still-buffered
     *  frame). Two steps, because `bufferedAmount` is unreliable on some WebRTC
     *  implementations (the node `wrtc` binding used in tests reports 0 even with
     *  a frame in flight): (1) drain any *reported* buffer (correct on browsers),
     *  then (2) a short bounded settle so the SCTP packet actually leaves before
     *  destroy(). Both are capped, so this can never hang close(). Verified: the
     *  server receives CLIENT_LEAVING and evicts immediately only with the
     *  settle; without it the frame races destroy() and is lost → grace path. */
    public override async flushBeforeLeave(): Promise<void> {
      await this.peerConnection?.flush();
      await asyncTimeout(WEBRTC_DELIBERATE_LEAVE_SETTLE_MS);
    }

    public onDisconnect() {
      if(this.peerConnection) {
        delete this.peerConnection.onPeerMessageHandler;
      }
      if(this.wsClient) {
        this.wsClient.onmessage = null;
      }
      if(this.wsClient?.readyState === WebSocket.CONNECTING || this.wsClient?.readyState === WebSocket.OPEN) {
        //console.log('CALL DISCONNECT HERE')  
        this.wsClient.close();
        delete this.wsClient;
      }
      if(this.peerConnection) {
        //console.log('DESTROY HERE');
        this.peerConnection.destroy();
        delete this.peerConnection;
      }
    }

    public send(data: ProtocolArray, reliable: boolean = false) {
      const payload = JSON.stringify(data);
      if(reliable) {
        this.peerConnection?.sendReliable(payload);
        return;
      }
      this.peerConnection?.sendUnreliable(payload);
    }

	// The binary fast path has no protocol envelope, so the target area rides in
	// the frame header (always written, even when absent — see frameRawWithArea).
	public override sendRaw(data: ArrayBuffer, reliable = true, _ensureOrdered = true, areaId?: string) {
		const framed = frameRawWithArea(data, areaId);
		if(reliable) {
			this.peerConnection?.sendReliable(framed);
		} else {
			this.peerConnection?.sendUnreliable(framed);
		}
	}

  private handlePeerMessage = (label: string, data: any) => {
    if(typeof data === 'string') {
      if(data === PING_STRINGED && label === WEBRTC_RELIABLE_CHANNEL) {
        this.peerConnection?.sendReliable(data);
        return;
      }
      this.handleStringPayload(data);
      return;
    }
    if(typeof Buffer !== 'undefined' && Buffer.isBuffer(data)) {
      const frame = ensureArrayBuffer(data as Buffer);
      this.handleRawFrame(frame);
      return;
    }
    if(data instanceof ArrayBuffer || ArrayBuffer.isView(data)) {
      const frame = ensureArrayBuffer(data as ArrayBuffer | ArrayBufferView);
      this.handleRawFrame(frame);
      return;
    }
    const fallback = data?.toString?.();
    if(fallback) {
      this.handleStringPayload(fallback);
    }
  }

	private handleStringPayload(message: string) {
		if(message === PING_STRINGED) {
			return;
		}
		try {
			const payload = JSON.parse(message);
			if(Array.isArray(payload)) {
				this.onServerMessage(payload as ProtocolArray);
			} else {
				console.warn('Received non-array payload on data channel', payload);
			}
		} catch (err) {
			console.error('Failed to parse server message', err);
		}
	}

  private handleRawFrame(frame: ArrayBuffer) {
    this.client.handleRawAreaMessage(frame);
  }
}